import { useCallback, useEffect, useState } from "react";
import { PackageOpen, RotateCcw, Search, Trash2 } from "lucide-react";
import { api, type GlobalTrashItem } from "./api";

const formatDate = (value: string) =>
  new Date(value).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export function GlobalTrash({
  onError,
}: {
  onError: (message: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<GlobalTrashItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState("");

  const load = useCallback(
    async (search = query) => {
      setLoading(true);
      try {
        const result = await api.globalTrash(search);
        setItems(result.items);
      } catch (error) {
        onError((error as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [onError, query],
  );

  useEffect(() => {
    const timeout = window.setTimeout(() => void load(query), 180);
    return () => window.clearTimeout(timeout);
  }, [load, query]);

  const restore = async (item: GlobalTrashItem) => {
    setRestoring(item.id);
    try {
      if (item.source === "maintenance_item") {
        if (!item.parentId)
          throw new Error("A manutenção vinculada não foi encontrada.");
        await api.restoreMaintenanceItem(item.parentId, item.id);
      } else if (item.source === "certification") {
        await api.restoreCertificationTrash(item.id);
      } else {
        await api.restoreRecordTrash(item.id);
      }
      await load(query);
    } catch (error) {
      onError((error as Error).message);
    } finally {
      setRestoring("");
    }
  };

  return (
    <section className="card global-trash">
      <header className="global-trash-heading">
        <div className="global-trash-title">
          <span className="global-trash-icon">
            <Trash2 />
          </span>
          <div>
            <span className="eyebrow">Retenção de 60 dias</span>
            <h2>Itens excluídos</h2>
            <p>
              Consulte e restaure registros removidos de todas as áreas do SGQ.
            </p>
          </div>
        </div>
        <strong>{loading ? "…" : items.length}</strong>
      </header>

      <div className="global-trash-toolbar">
        <label className="search global-trash-search">
          <Search />
          <input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar por nome, código, módulo, categoria ou responsável"
            aria-label="Buscar na lixeira"
          />
        </label>
        <span>
          {items.length} {items.length === 1 ? "registro" : "registros"}
        </span>
      </div>

      {loading ? (
        <div className="global-trash-empty">
          <PackageOpen />
          <p>Carregando lixeira…</p>
        </div>
      ) : items.length ? (
        <div className="table-wrap global-trash-table">
          <table>
            <thead>
              <tr>
                <th>Área</th>
                <th>Categoria</th>
                <th>Código</th>
                <th>Registro</th>
                <th>Excluído por</th>
                <th>Exclusão</th>
                <th>Disponível até</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={`${item.source}-${item.id}`}>
                  <td>{item.module}</td>
                  <td>
                    <span className="status-pill info">{item.category}</span>
                  </td>
                  <td className="mono">{item.code}</td>
                  <td>
                    <strong>{item.title}</strong>
                    {item.parentLabel && (
                      <small className="table-secondary">
                        {item.parentLabel}
                      </small>
                    )}
                  </td>
                  <td>{item.deletedByName}</td>
                  <td>{formatDate(item.deletedAt)}</td>
                  <td>{formatDate(item.expiresAt)}</td>
                  <td>
                    {item.canRestore ? (
                      <button
                        type="button"
                        className="button quiet"
                        disabled={Boolean(restoring)}
                        onClick={() => void restore(item)}
                      >
                        <RotateCcw />
                        {restoring === item.id ? "Restaurando…" : "Restaurar"}
                      </button>
                    ) : (
                      <span className="global-trash-readonly">Somente leitura</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="global-trash-empty">
          <Trash2 />
          <strong>{query ? "Nenhum resultado encontrado" : "A lixeira está vazia"}</strong>
          <p>
            {query
              ? "Tente buscar por outro nome, código ou módulo."
              : "Os registros excluídos aparecerão aqui durante o prazo de retenção."}
          </p>
        </div>
      )}
    </section>
  );
}
